/**
 * An alternate solution to `position: sticky` which uses `position: fixed` to allow elements to be
 * stuck to the viewport regardless of their containing elements. It's used to make the header
 * sticky without also affecting the global top banner.
 */

import throttle from 'lodash/throttle';
import { nextTick } from 'vue';
import getMedia from '@helpers/breakpoints';

const isDesktopMedia = getMedia('lg');

declare global {
  interface HTMLElement {
    _stickyDummy?: HTMLElement;
  }
}

export default function init(targets: HTMLElement[]) {
  // Set up dummy elements
  for (const target of targets) {
    makeDummy(target);
  }

  // Set up resize observer to update dummy heights
  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const target = entry.target as HTMLElement;
      const dummy = target._stickyDummy!;

      const newHeight = entry.borderBoxSize[0].blockSize;
      dummy.style.height = `${newHeight}px`;

      // Browser's scroll anchoring handles updating the scroll position, however, it unexpectedly
      // triggers a scroll event. Normally scroll anchoring doesn't trigger scroll events, it seems
      // to be related to `flex flex-col` on `<body>`, and `grow` on `<main>`. Initial investigation
      // showed that removing `grow` prevents the scroll event triggering. This is a workaround to
      // bail out of the scroll event.
      lastScrollY = window.scrollY;
    }
  });
  for (const target of targets) {
    resizeObserver.observe(target);
  }

  // Stick / unstick the target on scroll
  let lastScrollY = window.scrollY;
  document.addEventListener(
    'scroll',
    throttle(async () => {
      const currentScrollY = window.scrollY;
      const lastScrollYCopy = lastScrollY;
      // Update the last scroll position immediately in case this async function runs again before a
      // previous invocation has finished
      lastScrollY = currentScrollY;

      // User has scrolled down - hide the target and unstick it
      if (currentScrollY > lastScrollYCopy) {
        for (const target of targets) {
          if (target.classList.contains('is-stuck')) {
            // If dummy is out of view, slide up the target and collapse menus
            if (isScrolledAfterTarget(target)) {
              await slideUp(target);
            } // else it's in view, so just unstick it without animating

            unstick(target);
            // Force reflow, otherwise scroll anchoring doesn't seem to work for the nav (even
            // though it does for the header)
            target.getBoundingClientRect();
            await collapseMenus();
          }
        }
      }
      // User has scrolled up - show the target, stick it if the dummy is out of view
      else if (currentScrollY < lastScrollYCopy) {
        for (const target of targets) {
          /**
           * Stick the target if either:
           * - it's out of view
           * - it's already stuck and the dummy isn't entirely in view
           */
          const shouldBeStuck =
            isScrolledAfterTarget(target) ||
            (target.classList.contains('is-stuck') && !isScrolledBeforeTarget(target));

          if (shouldBeStuck) {
            if (!target.classList.contains('is-stuck')) {
              await collapseMenus();
              stick(target);
            }
          } else {
            unstick(target);
          }
        }
      }
      // else same position - possible when throttling
    }, 100),
  );
}

async function collapseMenus() {
  // Collapse header search
  const headerSearchTrigger = document.querySelector<HTMLButtonElement>(
    '.js-header-search-trigger',
  )!;
  if (headerSearchTrigger.ariaExpanded === 'true' && !isDesktopMedia.matches) {
    headerSearchTrigger.click();
  }

  // Collapse mobile nav
  const mobileNavTrigger = document.querySelector<HTMLButtonElement>(
    '.js-header-nav-mobile-trigger',
  )!;
  if (mobileNavTrigger.ariaExpanded === 'true') {
    mobileNavTrigger.click();
  }

  return nextTick();
}

/**
 * Creates a dummy element to take the place of the target element when it's stuck. Also required to
 * know when the element should be stuck, which won't be possible directly when it has position:
 * fixed.
 */
function makeDummy(target: HTMLElement) {
  const dummy = document.createElement('div');
  dummy.classList.add(
    'absolute',
    'top-0',
    'left-0',
    'right-0',
    'invisible',
    'peer-[.is-stuck]/target:static',
  );
  dummy.style.height = `${target.clientHeight}px`;
  const comment = document.createComment('Sticky dummy');
  dummy.appendChild(comment);

  target.classList.add('peer/target');
  target._stickyDummy = dummy;
  target.after(dummy);

  return dummy;
}

/**
 * Resolves when the transform transition on the target element has ended and accounts for if it has
 * no transition, eg. if the user has prefers-reduced-motion enabled. In that case it resolves
 * immediately.
 */
function transitionEnded(target: HTMLElement) {
  return new Promise<void>((resolve) => {
    const style = getComputedStyle(target);

    if (style.transitionDuration === '0s' || !style.transitionProperty.match(/\btransform\b/)) {
      return resolve();
    }

    const listener = (e: TransitionEvent) => {
      if (e.propertyName === 'transform') {
        target.removeEventListener('transitionend', listener);
        resolve();
      }
    };
    target.addEventListener('transitionend', listener);
  });
}

/**
 * Animates the target upwards beyond the viewport and tidies up afterwards by removing the
 * `-translate-y-full`.
 *
 * There's no equivalent `slideDown()` function as it's handled by `stick()` which always animates,
 * unlike unstick() which doesn't always. Sliding down also requires sticking the target *before*
 * sticking it, which makes it harder to decouple from the animation.
 */
async function slideUp(target: HTMLElement) {
  // Animate the target upwards beyong the viewport
  hide(target);
  // Wait for transition to end
  await transitionEnded(target);
  // Remove the translation
  show(target);
}

/**
 * Doe the viewport start after the target?
 */
function isScrolledAfterTarget(target: HTMLElement) {
  const dummy = target._stickyDummy!;
  return dummy.getBoundingClientRect().bottom < 0;
}

/**
 * Does the viewport start before the target?
 */
function isScrolledBeforeTarget(target: HTMLElement) {
  const dummy = target._stickyDummy!;
  return dummy.getBoundingClientRect().top > 0;
}

/**
 * Remove `-translate-y-full` to move the target into the viewport
 */
function show(target: HTMLElement) {
  target.classList.remove('is-hidden');
}

/**
 * Adds `-translate-y-full` to move the target out of the viewport
 */
function hide(target: HTMLElement) {
  target.classList.add('is-hidden');
}

/**
 * Sticks the target element to the top of the viewport and slides it down from the top.
 */
function stick(target: HTMLElement) {
  if (target.classList.contains('is-stuck')) return;

  // Temporarily disable the transition
  target.style.transitionProperty = 'none';
  // Translate out of the viewport so we can animate it in once it's stuck
  hide(target);

  // Force a reflow to ensure the transform is applied immediately
  target.getBoundingClientRect();
  // Re-add transition for the next transform (set with TW)
  target.style.transitionProperty = '';

  // Make target fixed and let the dummy take the height
  target.classList.add('is-stuck');

  // Animate in from top
  show(target);
}

/**
 * Unsticks the target element from the top of the viewport. Doesn't handle sliding the target up
 * and this is decoupled from sticking with slideUp().
 */
function unstick(target: HTMLElement) {
  // Make target static and take dummy out of the flow
  target.classList.remove('is-stuck');
}
